import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<'form' | 'confirm' | 'done'>('form');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) newErrors.name = 'お名前を入力してください';
    if (!formData.email.trim()) newErrors.email = 'メールアドレスを入力してください';
    if (!formData.message.trim()) newErrors.message = 'お問い合わせ内容を入力してください';
    if (formData.message.length > 500) newErrors.message = '500文字以内で入力してください';
    if (!agreed) newErrors.agreed = 'プライバシーポリシーに同意してください';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // まずバリデーション
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // 入力 → 確認画面へ
    if (step === 'form') {
      setErrors({});
      setStep('confirm');
      return;
    }

    // 確認画面 → 実際に送信
    if (step === 'confirm') {
      setIsSubmitting(true);

      try {
        const formBody = new URLSearchParams();
        formBody.append('name', formData.name);
        formBody.append('email', formData.email);
        formBody.append('message', formData.message);
        formBody.append('privacy', agreed ? '同意する' : '同意しない');

        const response = await fetch('/send.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString()
        });

        if (response.ok) {
          setStep('done');
          setFormData({ name: '', email: '', message: '' });
          setAgreed(false);
        } else {
          alert('送信に失敗しました。時間をおいて再度お試しください。');
        }
      } catch (error) {
        console.error('送信エラー:', error);
        alert('送信中にエラーが発生しました。');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          {/* タイトルまわりは元のまま */}
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: '"Noto Serif JP", serif' }}
            >
              お問い合わせ
            </h2>
            <div className="flex items-center justify-center gap-2">
              <div className="w-12 h-0.5 bg-amber-500"></div>
              <div className="w-2 h-2 bg-amber-500 rotate-45"></div>
              <div className="w-12 h-0.5 bg-amber-500"></div>
            </div>
          </div>

          <div className="bg-white shadow-xl border-l-4 border-amber-500 p-10">
            {/* ✅ 送信完了画面 */}
            {step === 'done' ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-amber-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-check-line text-4xl text-white"></i>
                </div>
                <h3
                  className="text-2xl font-bold text-slate-900 mb-4"
                  style={{ fontFamily: '"Noto Serif JP", serif' }}
                >
                  送信完了
                </h3>
                <p
                  className="text-slate-700 mb-8 leading-relaxed"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  お問い合わせありがとうございます。
                  <br />
                  担当者より折り返しご連絡させていただきます。
                </p>
                <button
                  onClick={() => setStep('form')}
                  className="px-10 py-3 bg-slate-900 text-white font-medium hover:bg-slate-800 transition-all duration-300 whitespace-nowrap shadow-lg hover:scale-105"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  新しいお問い合わせ
                </button>
              </div>
            ) : (
              // ✅ 入力画面 & 確認画面 共通フォーム
              <form
                id="eta-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {/* ▼ 入力ステップ */}
                {step === 'form' && (
                  <>
                    <div>
                      <label
                        className="block text-sm font-bold text-slate-700 mb-2"
                        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                      >
                        お名前 <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 focus:border-amber-500 focus:outline-none transition-colors duration-300"
                        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                      />
                      {errors.name && (
                        <p className="text-red-600 text-xs mt-1">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        className="block text-sm font-bold text-slate-700 mb-2"
                        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                      >
                        メールアドレス <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 focus:border-amber-500 focus:outline-none transition-colors duration-300"
                        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                      />
                      {errors.email && (
                        <p className="text-red-600 text-xs mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        className="block text-sm font-bold text-slate-700 mb-2"
                        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                      >
                        お問い合わせ内容 <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        maxLength={500}
                        rows={6}
                        className="w-full px-4 py-3 border-2 border-gray-200 focus:border-amber-500 focus:outline-none transition-colors duration-300 resize-none"
                        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                      ></textarea>
                      <div className="flex justify-between items-center mt-1">
                        {errors.message && (
                          <p className="text-red-600 text-xs">
                            {errors.message}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 ml-auto">
                          {formData.message.length}/500
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={e => {
                            setAgreed(e.target.checked);
                            if (errors.agreed)
                              setErrors(prev => ({ ...prev, agreed: '' }));
                          }}
                          className="mt-1 w-5 h-5 border-2 border-gray-300 cursor-pointer accent-amber-500"
                        />
                        <span
                          className="text-sm text-slate-700"
                          style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                        >
                          <a
                            href="/privacy"
                            className="text-amber-600 hover:text-amber-700 font-bold underline"
                          >
                            プライバシーポリシー
                          </a>
                          に同意します <span className="text-red-600">*</span>
                        </span>
                      </label>
                      {errors.agreed && (
                        <p className="text-red-600 text-xs mt-1">
                          {errors.agreed}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 bg-amber-500 text-slate-900 font-bold hover:bg-amber-400 transition-all duration-300 whitespace-nowrap shadow-lg hover:shadow-amber-500/50 hover:scale-105"
                      style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                    >
                      確認画面へ進む
                    </button>
                  </>
                )}

                {/* ▼ 確認ステップ */}
                {step === 'confirm' && (
                  <>
                    <p
                      className="text-sm text-slate-700 mb-4"
                      style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                    >
                      以下の内容で送信します。よろしければ「送信する」を押してください。
                    </p>

                    <div className="space-y-4 text-sm">
                      <div>
                        <span className="font-bold text-slate-800">
                          お名前
                        </span>
                        <p className="mt-1 text-slate-700 break-words">
                          {formData.name || '（未入力）'}
                        </p>
                      </div>
                      <div>
                        <span className="font-bold text-slate-800">
                          メールアドレス
                        </span>
                        <p className="mt-1 text-slate-700 break-words">
                          {formData.email || '（未入力）'}
                        </p>
                      </div>
                      <div>
                        <span className="font-bold text-slate-800">
                          お問い合わせ内容
                        </span>
                        <p className="mt-1 text-slate-700 whitespace-pre-wrap break-words">
                          {formData.message || '（未入力）'}
                        </p>
                      </div>
                      <div>
                        <span className="font-bold text-slate-800">
                          プライバシーポリシー同意
                        </span>
                        <p className="mt-1 text-slate-700">
                          {agreed ? '同意する' : '同意しない'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 mt-8">
                      <button
                        type="button"
                        onClick={() => setStep('form')}
                        className="w-full sm:w-1/2 py-3 border border-gray-300 text-slate-700 font-bold hover:bg-gray-50 transition-all duration-300"
                        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                      >
                        修正する
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full sm:w-1/2 py-3 bg-amber-500 text-slate-900 font-bold hover:bg-amber-400 transition-all duration-300 whitespace-nowrap shadow-lg hover:shadow-amber-500/50 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                      >
                        {isSubmitting ? '送信中...' : '送信する'}
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
